import { supabase } from "@/integrations/supabase/client";
import {
  evaluateContributionPostingBaseline,
  type ContributionPostingBaselineStatus,
} from "@/lib/finance-posting-baseline";

// Simple in-memory cache for fund restriction types.
// Maps `${orgId}::${fundId}` -> restriction_type. Keying by orgId+fundId
// prevents cross-tenant leakage when the same fund UUID exists in seed
// data across orgs, or when a user switches orgs mid-session.
const fundRestrictionCache = new Map<string, string | null>();

const fundRestrictionCacheKey = (orgId: string, fundId: string) =>
  `${orgId}::${fundId}`;

/**
 * Evict cached fund restriction lookups. Pass `(orgId, fundId)` to drop a
 * single entry (e.g., after editing the fund), `(orgId)` to drop the whole
 * tenant, or no args to clear everything (test reset / org switch).
 */
export function clearFundRestrictionCache(orgId?: string, fundId?: string) {
  if (!orgId) {
    fundRestrictionCache.clear();
    return;
  }
  if (fundId) {
    fundRestrictionCache.delete(fundRestrictionCacheKey(orgId, fundId));
    return;
  }
  const prefix = `${orgId}::`;
  for (const key of Array.from(fundRestrictionCache.keys())) {
    if (key.startsWith(prefix)) fundRestrictionCache.delete(key);
  }
}

export type FinanceQueueStatus =
  | "needs_review"
  | "needs_allocation"
  | "sync_failed"
  | "sync_pending"
  | "reviewed"
  | "ready_for_export";

export type FinanceQueueBulkAction = "review" | "allocate" | "retry_sync";

export interface FinanceQueueFundAllocation {
  /** Split identity: a contribution may have several splits for the same fund. */
  id?: string;
  fundId: string;
  fundName: string;
  amount: number;
  restrictionType?: string | null;
  revenueAccountCode?: string;
}

export interface FinanceQueueItem {
  id: string;
  organizationId: string;
  contributionId: string | null;
  depositId: string | null;
  depositStatus: string | null;
  donorName: string | null;
  /** Alias for donorName used by FinanceQueueTable */
  contributorName: string;
  amount: number | null;
  allocationTotal: number;
  allocationCount: number;
  postingBlocked: boolean;
  postingReason: string;
  postingBaselineStatus: ContributionPostingBaselineStatus;
  missingRequiredAllocation: boolean;
  journalEntryId: string | null;
  postingGroupId: string | null;
  sourceVersion: number;
  receivedAt: string | null;
  source: string | null;
  status: FinanceQueueStatus;
  lastError: string | null;
  paymentMethod: string;
  fundAllocations: FinanceQueueFundAllocation[];
}

/**
 * The period the month-end close is closing: an inclusive range of calendar
 * days, `YYYY-MM-DD`. Which entries belong to it is decided by the database
 * alone (see fetchUnpostedContributionEntriesPage); the client only names it.
 */
export interface FinanceQueueClosingPeriod {
  startDate: string;
  endDate: string;
}

export interface FinanceQueuePageParams {
  organizationId: string;
  page?: number;
  pageSize?: number;
  statuses?: FinanceQueueStatus[];
  /** Search the complete queue projection before counting and paginating. */
  search?: string;
  sort?: "newest" | "oldest" | "amount_desc" | "amount_asc";
  /**
   * Only entries that have not been posted to the ledger yet
   * (`journal_entry_id IS NULL`). Posting sets `journal_entry_id` and leaves
   * `status` alone, so an already-posted entry keeps reading
   * `ready_for_export` forever. This is a plain filter for the whole-queue
   * view; a closing period is answered by the database's own rule instead
   * (fetchUnpostedContributionEntriesPage), never by combining filters here.
   */
  unpostedOnly?: boolean;
}

export interface FinanceQueuePageResult {
  data: FinanceQueueItem[];
  total: number;
  page: number;
  pageSize: number;
}

export type FinanceQueueStatusCounts = Record<FinanceQueueStatus, number> & {
  all: number;
};

export const STATUS_PRIORITY: Record<FinanceQueueStatus, number> = {
  sync_failed: 0,
  needs_allocation: 1,
  needs_review: 2,
  sync_pending: 3,
  reviewed: 4,
  ready_for_export: 5,
};

/**
 * Determines which revenue account to use for a fund allocation based on its restriction type.
 * - Unrestricted funds → Account 4000 (Contributions - General)
 * - Restricted funds (donor_purpose, donor_time, donor_permanent) → Account 4100 (Contributions - Restricted)
 * - Board-designated → Account 4000 (treated as unrestricted)
 */
const RESTRICTED_TYPES = new Set([
  "donor_purpose",
  "donor_time",
  "donor_permanent",
]);

const accountForRestrictionType = (
  restrictionType: string | null | undefined,
): { accountCode: string; accountId: null } => {
  const isRestricted = RESTRICTED_TYPES.has(restrictionType ?? "");
  return { accountCode: isRestricted ? "4100" : "4000", accountId: null };
};

export async function getRevenueAccountForFund(
  orgId: string,
  fundDimensionId: string,
): Promise<{ accountCode: string; accountId: string | null }> {
  if (!orgId || !fundDimensionId) {
    throw new Error(
      "getRevenueAccountForFund requires both orgId and fundDimensionId",
    );
  }

  // Check cache first — keyed by orgId+fundId so the same fund UUID across
  // tenants does not leak.
  const cacheKey = fundRestrictionCacheKey(orgId, fundDimensionId);
  const cached = fundRestrictionCache.get(cacheKey);
  if (cached !== undefined) {
    return accountForRestrictionType(cached);
  }

  // Query the finance_dimensions table for the fund's restriction_type.
  const { data, error } = await supabase
    .from("finance_dimensions")
    .select("id, restriction_type")
    .eq("id", fundDimensionId)
    .eq("org_id", orgId)
    .maybeSingle();

  if (error) {
    // Bubble the error up rather than silently misclassifying restricted
    // donor revenue as unrestricted (account 4000). The caller is in a
    // better position to retry, surface the failure, or block posting.
    console.error("Error fetching fund restriction type:", error);
    throw new Error(
      `Failed to look up fund restriction type for ${fundDimensionId}: ${
        error.message ?? "unknown error"
      }`,
    );
  }

  if (!data) {
    // Fund row not found / not visible to this org — refuse to guess. A
    // non-existent fund silently classified as unrestricted is a financial
    // accuracy bug, not a usability one.
    throw new Error(
      `Fund ${fundDimensionId} not found in org ${orgId}`,
    );
  }

  const restrictionType = data.restriction_type ?? "unrestricted";
  fundRestrictionCache.set(cacheKey, restrictionType);
  return accountForRestrictionType(restrictionType);
}

// Use a finite sentinel rather than -Infinity so that
//   getReceivedAtValue(null) - getReceivedAtValue(null) === 0
// instead of NaN (Infinity - Infinity), which makes Array.sort
// non-deterministic and produces unstable ordering of null/invalid timestamps.
const RECEIVED_AT_SENTINEL = -8.64e15; // matches Date min (-100,000,000 days)

const getReceivedAtValue = (receivedAt: string | null) => {
  if (!receivedAt) return RECEIVED_AT_SENTINEL;
  const parsed = Date.parse(receivedAt);
  return Number.isNaN(parsed) ? RECEIVED_AT_SENTINEL : parsed;
};


interface FinanceQueueProjectionRow {
  id: string;
  organization_id: string;
  contribution_id: string | null;
  donor_name: string | null;
  donor_person_name: string | null;
  amount: number | null;
  received_at: string | null;
  source: string | null;
  status: FinanceQueueStatus;
  last_error: string | null;
  source_version: number;
  posting_group_id: string | null;
  journal_entry_id: string | null;
  deposit_id: string | null;
  deposit_status: string | null;
  allocation_count: number;
  allocation_total: number;
  has_missing_allocation: boolean;
  // payment_method is not currently included in the SELECT list, so it will
  // arrive as undefined on every row. Typed as optional so the field falls
  // through to the `"other"` default cleanly. If the view starts projecting
  // it, both the SELECT strings AND this type need updating in lockstep.
  payment_method?: string | null;
}

/**
 * Coerce a raw value to a finite number. Returns the fallback for NaN /
 * undefined / non-numeric strings so a malformed allocation_total cell can't
 * propagate NaN into the posting-baseline check (which would silently mark
 * entries as ready-to-post when in fact totals are unverifiable).
 */
const toFiniteNumber = (raw: unknown, fallback: number): number => {
  if (raw === null || raw === undefined) return fallback;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : fallback;
};

// Status not in the priority map (defensive: if a new status is added to the
// DB before the client is updated). Sort unknowns last so they don't
// pre-empt sync_failed/needs_allocation work.
const UNKNOWN_PRIORITY = Number.POSITIVE_INFINITY;
const priorityFor = (status: FinanceQueueStatus) =>
  STATUS_PRIORITY[status] ?? UNKNOWN_PRIORITY;

export const sortByUrgency = (items: FinanceQueueItem[]) =>
  [...items].sort((a, b) => {
    const priorityDelta = priorityFor(a.status) - priorityFor(b.status);
    if (priorityDelta !== 0) {
      return priorityDelta;
    }

    const timeDelta =
      getReceivedAtValue(b.receivedAt) - getReceivedAtValue(a.receivedAt);
    if (timeDelta !== 0) {
      return timeDelta;
    }

    // Final deterministic tiebreaker on id so identical priority+timestamp
    // never produces a non-stable order across renders.
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  });

const actionStatusMap: Record<FinanceQueueBulkAction, FinanceQueueStatus> = {
  review: "reviewed",
  allocate: "ready_for_export",
  retry_sync: "sync_pending",
};

const mapQueueRow = (row: FinanceQueueProjectionRow): FinanceQueueItem => {
  const allocationTotal = toFiniteNumber(row.allocation_total, 0);
  const allocationCount = Math.max(0, Math.trunc(toFiniteNumber(row.allocation_count, 0)));
  // amount may be NaN if the DB returns a string like "abc" (shouldn't happen,
  // but Postgres numeric → JS number conversion has historically surfaced
  // surprises). Pass `null` rather than NaN to the baseline evaluator so the
  // missingRequiredAllocation check reads as `0 > 0 → false` consistently.
  const safeAmount = row.amount === null || row.amount === undefined
    ? null
    : (Number.isFinite(Number(row.amount)) ? Number(row.amount) : null);

  const postingBaseline = evaluateContributionPostingBaseline({
    queueStatus: row.status ?? null,
    amount: safeAmount,
    allocationTotal,
    hasAllocations: allocationCount > 0,
    depositId: row.deposit_id ?? null,
    journalEntryId: row.journal_entry_id ?? null,
  });

  const sourceVersionRaw = toFiniteNumber(row.source_version, 1);
  const sourceVersion = sourceVersionRaw >= 1 ? Math.trunc(sourceVersionRaw) : 1;

  // The view projects no payment_method; `source` carries the contribution's method
  // (trg_auto_create_finance_contribution_entry writes COALESCE(NEW.method, 'manual')).
  const paymentMethodRaw = row.payment_method || row.source;
  const paymentMethod =
    typeof paymentMethodRaw === "string" && paymentMethodRaw.length > 0
      ? paymentMethodRaw
      : "other";

  return {
    id: row.id,
    organizationId: row.organization_id,
    contributionId: row.contribution_id ?? null,
    depositId: row.deposit_id ?? null,
    depositStatus: row.deposit_status ?? null,
    donorName: row.donor_name ?? row.donor_person_name ?? null,
    amount: safeAmount,
    allocationTotal,
    allocationCount,
    postingBlocked: postingBaseline.isPostingBlocked,
    postingReason: postingBaseline.reason,
    postingBaselineStatus: postingBaseline.status,
    missingRequiredAllocation: postingBaseline.missingRequiredAllocation || Boolean(row.has_missing_allocation),
    journalEntryId: row.journal_entry_id ?? null,
    postingGroupId: row.posting_group_id ?? null,
    sourceVersion,
    receivedAt: row.received_at ?? null,
    source: row.source ?? null,
    status: (row.status ?? "needs_review") as FinanceQueueStatus,
    lastError: row.last_error ?? null,
    contributorName: row.donor_name ?? row.donor_person_name ?? "Unknown",
    paymentMethod,
    fundAllocations: [],
  };
};

// Reasonable upper bound on a single page. PostgREST's default and Supabase's
// hard cap is 1000; allowing larger values would either be silently clamped
// or trigger a 416 Range error.
const MAX_PAGE_SIZE = 1000;

export async function fetchFinanceQueuePage({
  organizationId,
  page = 1,
  pageSize = 50,
  statuses,
  search,
  sort = "newest",
  unpostedOnly,
}: FinanceQueuePageParams): Promise<FinanceQueuePageResult> {
  if (!organizationId) {
    throw new Error("fetchFinanceQueuePage requires organizationId");
  }

  // Coerce and clamp inputs. Math.max alone would still let through floats
  // (page = 1.7 → from = 0.7 * pageSize, weird .range() call) and
  // unboundedly-large pageSize → Supabase 416 / huge payload.
  const safePage = Math.max(1, Math.trunc(Number.isFinite(Number(page)) ? Number(page) : 1));
  const safePageSize = Math.max(
    1,
    Math.min(
      MAX_PAGE_SIZE,
      Math.trunc(Number.isFinite(Number(pageSize)) ? Number(pageSize) : 50),
    ),
  );
  const from = (safePage - 1) * safePageSize;
  const to = from + safePageSize - 1;

  // Always ask for the exact count. The previous behavior (count only on
  // page 1) meant `total` fell back to `mapped.length` on every subsequent
  // page, breaking pagination UI ("Page 2 of 50" rendered as "Page 2 of 50"
  // on page 1, but "Page 2 of 50" on page 2 due to the 50-row payload).
  // The "head"-style count requests in fetchFinanceQueueStatusCounts do the
  // heavy lifting separately; here the cost is bounded since we're already
  // on the same query.
  const query = supabase
    .from("v_finance_queue")
      .select(
        `
        id,
        organization_id,
        contribution_id,
        donor_name,
        amount,
        received_at,
        source,
        status,
        last_error,
        source_version,
        posting_group_id,
        journal_entry_id,
        deposit_id,
        deposit_status,
        allocation_count,
        allocation_total,
        has_missing_allocation,
        donor_person_name
      `,
      { count: "exact" }
    )
    .eq("organization_id", organizationId)
    .order(sort === "amount_desc" || sort === "amount_asc" ? "amount" : "received_at", {
      ascending: sort === "oldest" || sort === "amount_asc", nullsFirst: false,
    });

  if (sort === "amount_desc" || sort === "amount_asc") {
    query.order("received_at", { ascending: false, nullsFirst: false });
  }
  // Stable ties and exact count apply to the same server-filtered result.
  query.order("id", { ascending: true }).range(from, to);

  if (search?.trim()) {
    // Quotes protect the PostgREST boolean grammar (commas, parentheses, dots).
    // Escape quotes/backslashes within the value; the SDK handles URL encoding.
    // LIKE wildcards retain their usual semantics, as on existing finance lists.
    const pattern = `"%${search.trim().replace(/\\/g, "\\\\").replace(/"/g, '\\"')}%"`;
    query.or(["donor_name", "donor_person_name", "source", "last_error"]
      .map((column) => `${column}.ilike.${pattern}`).join(","));
  }

  if (statuses && statuses.length > 0) {
    query.in("status", statuses);
  }

  if (unpostedOnly === true) {
    query.is("journal_entry_id", null);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error("Error fetching finance queue:", error);
    throw error;
  }

  const mapped = (data ?? []).map((row) => mapQueueRow(row));

  return {
    data: mapped,
    total: count ?? mapped.length,
    page: safePage,
    pageSize: safePageSize,
  };
}

export async function fetchFinanceQueueStatusCounts(
  organizationId: string
): Promise<FinanceQueueStatusCounts> {
  const statuses: FinanceQueueStatus[] = [
    "needs_review",
    "needs_allocation",
    "sync_failed",
    "sync_pending",
    "reviewed",
    "ready_for_export",
  ];

  const countEntries = await Promise.all(
    statuses.map(async (status) => {
      const { count, error } = await supabase
        .from("v_finance_queue")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", organizationId)
        .eq("status", status);

      if (error) {
        console.error("Error fetching finance queue status counts:", error);
        throw error;
      }

      return [status, count ?? 0] as const;
    })
  );

  const counts = countEntries.reduce<Record<string, number>>((acc, [status, count]) => {
    acc[status] = count;
    return acc;
  }, {});

  const all = Object.values(counts).reduce((total, current) => total + current, 0);

  return {
    all,
    needs_review: counts.needs_review ?? 0,
    needs_allocation: counts.needs_allocation ?? 0,
    sync_failed: counts.sync_failed ?? 0,
    sync_pending: counts.sync_pending ?? 0,
    reviewed: counts.reviewed ?? 0,
    ready_for_export: counts.ready_for_export ?? 0,
  };
}

// Chunk size for `.in("id", ids)` calls. PostgREST URLs have practical length
// limits and Supabase's WHERE-IN handling can degrade well before Postgres'
// own ~32k parameter cap. 500 keeps the URL well under common 8KB limits.
const IN_CLAUSE_CHUNK_SIZE = 500;

const chunk = <T,>(arr: T[], size: number): T[][] => {
  if (size <= 0) return [arr];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export async function fetchFinanceQueueBatch({
  organizationId,
  ids,
}: {
  organizationId: string;
  ids: string[];
}): Promise<FinanceQueueItem[]> {
  if (!organizationId) {
    throw new Error("fetchFinanceQueueBatch requires organizationId");
  }
  // Filter empty / non-string ids before round-tripping. Sending [""] to
  // `.in("id", ...)` produces a confusing 400 on UUID columns.
  const cleanIds = Array.from(new Set(
    ids.filter((id): id is string => typeof id === "string" && id.length > 0),
  ));
  if (cleanIds.length === 0) return [];

  const aggregated: FinanceQueueProjectionRow[] = [];
  for (const idChunk of chunk(cleanIds, IN_CLAUSE_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from("v_finance_queue")
      .select(
        `
        id,
        organization_id,
        contribution_id,
        donor_name,
        amount,
        received_at,
        source,
        status,
        last_error,
        source_version,
        posting_group_id,
        journal_entry_id,
        deposit_id,
        deposit_status,
        allocation_count,
        allocation_total,
        has_missing_allocation,
        donor_person_name
      `
      )
      .eq("organization_id", organizationId)
      .in("id", idChunk);

    if (error) {
      console.error("Error fetching finance queue batch:", error);
      throw error;
    }
    aggregated.push(...((data ?? []) as FinanceQueueProjectionRow[]));
  }

  return aggregated.map((row) => mapQueueRow(row));
}

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True when the period list was refused because of WHO is asking, not because
 * something failed: the database answers a caller outside finance, or one that can
 * count the entries but is not allowed to open them, with `42501` or "Not authorized
 * for this organization". (Since #6495 no role the count admits is refused the list,
 * the treasurer included; the second case stays as a guard if the two role lists drift.)
 * A retry cannot change that answer.
 *
 * Deliberately narrower than isSupabasePermissionError, which also matches 401/403,
 * PGRST301 and "not authenticated": an expired session or a dropped connection is
 * not a role problem, and telling someone their role cannot open the entries when
 * their login lapsed would send them to the wrong person.
 */
export function isQueueAccessRefusedError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (code === "42501") return true;
  return typeof message === "string" && /not authorized for this organization/i.test(message);
}

/**
 * One page of the contribution entries that are ready to post but not posted,
 * for the period the month-end close is closing. This is the list behind the
 * close page's "All gifts posted to the ledger" blocker.
 *
 * WHICH entries belong to the period is decided in the database and nowhere
 * else: `finance_list_unposted_contribution_entries` pages over the same
 * `finance_unposted_contribution_entry_ids_unchecked` rule the close blocker
 * counts with, so this list and that count cannot disagree. The rule dates an
 * entry by where posting will BOOK it (an entry received in a hard-closed
 * period is moved onto today, or refused when today is closed too) and ignores
 * entries whose gift was deleted. None of that is reproducible from
 * `received_at` on the client, so nothing here filters by date or status: the
 * RPC returns the ids, in order, with the size of the whole result, and this
 * function only fetches the queue rows for those ids.
 *
 * `total` is the RPC's total for the whole period, not the length of `data`.
 * A page past the end comes back from the RPC as one row with a null id and
 * the total; that row is dropped here, so `data` is empty and `total` still
 * says how many entries exist, which is what lets the caller step back to the
 * last page that has rows.
 *
 * Fails closed: if the queue rows for the returned ids do not all come back
 * (a row voided between the two reads, a row the caller cannot see), it throws
 * rather than return a shorter list beside a total that counts the missing
 * ones, which would read as a smaller backlog than the one the close is
 * blocked on. It throws the same way when a row comes back already posted or no
 * longer ready to post (another session acted between the two reads), rather
 * than list it as waiting.
 */
export async function fetchUnpostedContributionEntriesPage({
  organizationId,
  period,
  page = 1,
  pageSize = 50,
}: {
  organizationId: string;
  period: FinanceQueueClosingPeriod;
  page?: number;
  pageSize?: number;
}): Promise<FinanceQueuePageResult> {
  if (!organizationId) {
    throw new Error("fetchUnpostedContributionEntriesPage requires organizationId");
  }
  if (
    !period ||
    !YMD_PATTERN.test(period.startDate) ||
    !YMD_PATTERN.test(period.endDate) ||
    period.startDate > period.endDate
  ) {
    throw new Error(
      "fetchUnpostedContributionEntriesPage requires period as YYYY-MM-DD dates with startDate on or before endDate",
    );
  }

  const safePage = Math.max(1, Math.trunc(Number.isFinite(Number(page)) ? Number(page) : 1));
  const safePageSize = Math.max(
    1,
    Math.min(
      MAX_PAGE_SIZE,
      Math.trunc(Number.isFinite(Number(pageSize)) ? Number(pageSize) : 50),
    ),
  );

  const { data: listed, error } = await supabase.rpc("finance_list_unposted_contribution_entries", {
    _org_id: organizationId,
    _period_start: period.startDate,
    _period_end: period.endDate,
    _limit: safePageSize,
    _offset: (safePage - 1) * safePageSize,
  });

  if (error) {
    console.error("Error listing unposted contribution entries:", error);
    throw error;
  }

  // The function always answers with at least one row (the sentinel on an empty
  // page). An empty answer is not "zero entries", it is a malformed one, and
  // reading it as zero would be a false all-clear.
  if (!Array.isArray(listed) || listed.length === 0) {
    throw new Error("finance_list_unposted_contribution_entries returned no rows");
  }

  const total = Number(listed[0].total_count);
  if (!Number.isFinite(total) || total < 0) {
    throw new Error("finance_list_unposted_contribution_entries returned an invalid total");
  }

  const orderedIds = listed
    .map((row) => row.entry_id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);

  if (orderedIds.length === 0) {
    return { data: [], total, page: safePage, pageSize: safePageSize };
  }

  const rows = await fetchFinanceQueueBatch({ organizationId, ids: orderedIds });
  const byId = new Map(rows.map((row) => [row.id, row]));
  const data: FinanceQueueItem[] = [];
  for (const id of orderedIds) {
    const row = byId.get(id);
    if (!row) {
      throw new Error(
        "Some entries counted as ready to post could not be loaded. Try again.",
      );
    }
    // The list and these rows are two reads. Posting sets `journal_entry_id` and leaves
    // `status` alone, so an entry another session posted in between is still returned by
    // the view, looking like every other ready entry. Listing it beside the earlier total
    // would offer a posted entry as waiting. Refuse the page instead: the next read is
    // consistent, and the default query retry asks again on its own.
    if (row.status !== "ready_for_export" || Boolean(row.journalEntryId)) {
      throw new Error("Some entries were posted or changed while the list loaded. Try again.");
    }
    data.push(row);
  }

  return { data, total, page: safePage, pageSize: safePageSize };
}

export async function applyFinanceQueueBulkAction({
  organizationId,
  ids,
  action,
}: {
  organizationId: string;
  ids: string[];
  action: FinanceQueueBulkAction;
}) {
  if (!organizationId) {
    throw new Error("applyFinanceQueueBulkAction requires organizationId");
  }
  // Defensive: validate the action up-front. A bogus value would otherwise
  // resolve to `nextStatus = undefined` and either NULL the column or 400.
  const nextStatus = actionStatusMap[action];
  if (!nextStatus) {
    throw new Error(`Unknown finance queue bulk action: ${String(action)}`);
  }

  const cleanIds = Array.from(new Set(
    ids.filter((id): id is string => typeof id === "string" && id.length > 0),
  ));
  if (cleanIds.length === 0) return [];

  const updatePayload: Record<string, unknown> = {
    status: nextStatus,
    ...(action === "retry_sync" ? { last_error: null } : {}),
  };

  // State-machine guard. Without this, callers can:
  //   - move a posted (journal_entry_id IS NOT NULL) row back to "reviewed"
  //   - retry-sync a row that already succeeded, demoting it to sync_pending
  //     and clearing its last_error
  // Both are silent corruption of the audit trail. Filter to rows that are
  // legitimately in a state where this transition makes sense:
  //   - `review` and `allocate`: not already posted (journal_entry_id IS NULL)
  //   - `retry_sync`: was actually in a failed state
  let query = (supabase
    .from("finance_contribution_entries" as string) as ReturnType<typeof supabase.from>)
    .update(updatePayload)
    .eq("org_id", organizationId)
    .in("id", cleanIds);

  if (action === "retry_sync") {
    query = query.eq("status", "sync_failed");
  } else {
    // `review` / `allocate` should not stomp posted rows.
    query = query.is("journal_entry_id", null);
  }

  const { data, error } = await query.select(`
      id,
      org_id,
      contribution_id,
      donor_name,
      amount,
      received_at,
      source,
      status,
      last_error,
      source_version,
      posting_group_id,
      journal_entry_id,
      deposit_batch_id
    `);

  if (error) {
    console.error("Error updating finance queue:", error);
    throw error;
  }

  const updatedIds = (data ?? [])
    .map((row: { id: unknown }) => row?.id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);

  if (updatedIds.length === 0) return [];

  const hydrated = await fetchFinanceQueueBatch({
    organizationId,
    ids: updatedIds,
  });
  return hydrated;
}

// ── Post contributions to GL ─────────────────────────────────────────────

export interface PostContributionsErrorDetail {
  contributionEntryId: string;
  error: string;
}

export interface PostContributionsResult {
  posted: number;
  skipped: number;
  /**
   * Backwards-compatible flat error messages. Use `errorDetails` when you
   * need to know *which* contribution entry failed (e.g. to navigate the
   * user to that row).
   */
  errors: string[];
  errorDetails: PostContributionsErrorDetail[];
}

/** Raw shape returned by the finance_post_contributions RPC */
interface PostContributionsRpcResult {
  posted: number;
  skipped: number;
  errors: number;
  error_details: { contribution_entry_id: string; error: string }[];
  org_id: string;
}

/**
 * Post contribution entries to the general ledger by calling the
 * `finance_post_contributions` RPC. Each entry must be in
 * `ready_for_export` status.
 */
export async function postContributions({
  organizationId,
  entryIds,
}: {
  organizationId: string;
  entryIds: string[];
}): Promise<PostContributionsResult> {
  if (!organizationId) {
    throw new Error("postContributions requires organizationId");
  }
  // Drop empty / non-string ids before sending. The RPC sees `[""]` as a
  // legitimate input and returns confusing "entry not found" errors that
  // look like real data problems.
  const cleanIds = Array.from(new Set(
    entryIds.filter((id): id is string => typeof id === "string" && id.length > 0),
  ));
  if (cleanIds.length === 0) {
    return { posted: 0, skipped: 0, errors: [], errorDetails: [] };
  }

  const { data, error } = await supabase.rpc(
    "finance_post_contributions" as string,
    {
      p_org_id: organizationId,
      p_contribution_entry_ids: cleanIds,
    },
  ) as { data: PostContributionsRpcResult | null; error: { message?: string } | null };

  if (error) {
    console.error("Error posting contributions to GL:", error);
    throw new Error(error.message || "Failed to post contributions");
  }

  if (!data) {
    return { posted: 0, skipped: 0, errors: [], errorDetails: [] };
  }

  const postedRaw = toFiniteNumber(data.posted, 0);
  const skippedRaw = toFiniteNumber(data.skipped, 0);
  const detailsRaw = Array.isArray(data.error_details) ? data.error_details : [];

  const errorDetails: PostContributionsErrorDetail[] = detailsRaw.map((e) => ({
    contributionEntryId:
      typeof e?.contribution_entry_id === "string" ? e.contribution_entry_id : "",
    error: typeof e?.error === "string" && e.error.length > 0 ? e.error : "Unknown posting error",
  }));

  return {
    posted: Math.max(0, Math.trunc(postedRaw)),
    skipped: Math.max(0, Math.trunc(skippedRaw)),
    errors: errorDetails.map((d) => d.error),
    errorDetails,
  };
}

/**
 * Resolve the GL-ready finance entries the `trg_auto_create_finance_entry`
 * trigger created for the given contributions, then post them to the General
 * Ledger. This is the single correct auto-post path shared by every
 * contribution-entry surface (quick / batch / fast).
 *
 * Why it exists: `finance_post_contributions` matches on
 * `finance_contribution_entries.id` — NOT `contributions.id`. Callers must map
 * contribution ids → `ready_for_export` entry ids before posting. Quick-entry
 * did this inline; batch-entry passed contribution ids straight through as
 * `entryIds`, so the RPC matched nothing and silently posted zero. Centralising
 * the mapping here fixes that and gives fast-entry the same behaviour.
 *
 * Scoping mirrors the quick-entry query: filter by `contribution_id` + status
 * and rely on RLS for the org boundary (the entries table is keyed `org_id`,
 * and the contribution ids are already org-scoped by their batch).
 */
export async function autoPostContributionsByContributionIds({
  organizationId,
  contributionIds,
}: {
  organizationId: string;
  contributionIds: string[];
}): Promise<PostContributionsResult> {
  const ids = Array.from(
    new Set(
      (contributionIds ?? []).filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      ),
    ),
  );
  if (!organizationId || ids.length === 0) {
    return { posted: 0, skipped: 0, errors: [], errorDetails: [] };
  }

  const { data: financeEntries, error } = await supabase
    .from("finance_contribution_entries")
    .select("id")
    .in("contribution_id", ids)
    .eq("status", "ready_for_export");

  if (error) {
    console.error("Failed to resolve finance entries for auto-post:", error);
    throw new Error(error.message || "Failed to resolve contribution entries for posting");
  }

  const entryIds = (financeEntries ?? []).map((e: { id: string }) => e.id);
  if (entryIds.length === 0) {
    return { posted: 0, skipped: 0, errors: [], errorDetails: [] };
  }

  return postContributions({ organizationId, entryIds });
}

// ── Reverse / restore contribution GL postings ───────────────────────────────

/**
 * `finance_reverse_contribution_postings` and
 * `finance_restore_contribution_postings` are newer than the last
 * `supabase gen types` run, so they are absent from the generated `Database`
 * union and `supabase.rpc(name, …)` rejects their names outright. Route them
 * through a narrowly-typed accessor, mirroring `supabaseUntyped` in
 * finance-posting.ts. Note that the `as string` cast used by
 * `postContributions` above does NOT satisfy the overload — it is a live
 * TS2345 in the baseline — so it is not the pattern to copy.
 *
 * Delete this and call `supabase.rpc` directly once the generated types are
 * regenerated against the migration that adds these two functions.
 */
const supabaseRpcUntyped = supabase as unknown as {
  rpc: (
    fn: string,
    args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message?: string } | null }>;
};

export interface ContributionReversalResult {
  /** Contributions whose posted journal entry was reversed by a compensating entry. */
  reversed: number;
  /**
   * Contributions that had never been posted to the GL. Nothing needed
   * reversing, but their subledger entry is now void so they can't be posted
   * after the fact.
   */
  voidedUnposted: number;
  /** Ids with no non-void subledger entry when the call started — already reversed, or never had one. */
  skipped: number;
}

export interface ContributionDeleteResult {
  /** The ids actually soft-deleted (an already-deleted id is not re-deleted). */
  deletedIds: string[];
  deleted: number;
  reversed: number;
  voidedUnposted: number;
  /**
   * Only ever non-empty when `requireUnposted` was passed: contributions
   * whose entry turned out already posted, checked under lock before the
   * RPC ever deleted anything -- so they were never deleted at all, not
   * deleted then undone. See finance_delete_contributions_with_reversal's
   * p_require_unposted (20260915201615, corrected in 20260915205156).
   */
  skippedPostedIds: string[];
  skippedPosted: number;
}

export interface ContributionUndeleteResult {
  restoredIds: string[];
  undeleted: number;
  restored: number;
  unvoidedUnposted: number;
}

/**
 * Raw jsonb from the ledger RPCs. They report only counts and ids — per-entry
 * failures raise instead, so there is no error-details channel to map.
 */
interface LedgerRpcResult {
  deleted_ids?: unknown;
  restored_ids?: unknown;
  deleted?: unknown;
  undeleted?: unknown;
  reversed?: unknown;
  restored?: unknown;
  voided_unposted?: unknown;
  unvoided_unposted?: unknown;
  skipped?: unknown;
  skipped_posted_ids?: unknown;
  skipped_posted?: unknown;
}

/** Coerce an RPC counter to a non-negative integer, never NaN. */
function toCount(raw: unknown): number {
  return Math.max(0, Math.trunc(toFiniteNumber(raw, 0)));
}

/** Strip empty/non-string ids the same way postContributions does. */
function cleanContributionIds(ids: string[]): string[] {
  return Array.from(
    new Set((ids ?? []).filter((id): id is string => typeof id === "string" && id.length > 0)),
  );
}

/**
 * Soft-delete contributions AND reverse their General Ledger postings, in one
 * transaction.
 *
 * This is a single RPC rather than a delete call followed by a reversal call,
 * and that matters. Two client-sequenced calls leave a window where the
 * contribution is hidden and the ledger is not reversed — the exact state this
 * exists to prevent — and a compensating "put the reversal back" step does not
 * close it, because that step can fail too. Inside one function both writes
 * commit or neither does.
 *
 * Authorization is whatever `set_contributions_deleted_at` allows, because the
 * RPC delegates to it rather than adding a second finance-role gate. A second
 * gate would have been narrower than the delete it fronts, and would have
 * broken deletion for platform operators and agency operate-as sessions whose
 * authority comes from `get_user_write_org_ids` rather than org membership.
 *
 * Per-entry failures are NOT returned as data: the RPC raises, the transaction
 * rolls back, and this throws. A reversal that fails must not leave a delete
 * standing over a live journal entry.
 */
export async function deleteContributionsWithReversal({
  organizationId,
  contributionIds,
  deletedAt,
  reason,
  requireUnposted,
}: {
  organizationId: string;
  contributionIds: string[];
  deletedAt: string;
  reason?: string;
  /**
   * When true, the RPC locks and partitions by posted status BEFORE
   * deleting anything -- a contribution whose entry turns out already
   * posted is never deleted at all (checked under a fresh row lock inside
   * the RPC's own transaction, not this call's possibly-stale view), and
   * comes back in `skippedPostedIds` rather than `deletedIds`. Default
   * false preserves the original behavior (delete then reverse regardless
   * of posted status) for every existing caller.
   */
  requireUnposted?: boolean;
}): Promise<ContributionDeleteResult> {
  if (!organizationId) {
    throw new Error("deleteContributionsWithReversal requires organizationId");
  }
  const ids = cleanContributionIds(contributionIds);
  if (ids.length === 0) {
    return { deletedIds: [], deleted: 0, reversed: 0, voidedUnposted: 0, skippedPostedIds: [], skippedPosted: 0 };
  }

  const { data, error } = (await supabaseRpcUntyped.rpc(
    "finance_delete_contributions_with_reversal",
    {
      p_organization_id: organizationId,
      p_ids: ids,
      p_deleted_at: deletedAt,
      p_reason: reason ?? null,
      p_require_unposted: requireUnposted ?? false,
    },
  )) as { data: LedgerRpcResult | null; error: { message?: string } | null };

  if (error) {
    console.error("Error deleting contributions with GL reversal:", error);
    throw new Error(error.message || "Failed to delete contributions");
  }

  return {
    deletedIds: Array.isArray(data?.deleted_ids)
      ? data.deleted_ids.filter((id): id is string => typeof id === "string")
      : [],
    deleted: toCount(data?.deleted),
    reversed: toCount(data?.reversed),
    voidedUnposted: toCount(data?.voided_unposted),
    skippedPostedIds: Array.isArray(data?.skipped_posted_ids)
      ? data.skipped_posted_ids.filter((id): id is string => typeof id === "string")
      : [],
    skippedPosted: toCount(data?.skipped_posted),
  };
}

/**
 * Inverse of {@link deleteContributionsWithReversal}, for the delete-undo path.
 *
 * Clears `deleted_at` and posts a re-instating journal entry in one
 * transaction, so undo cannot bring the gift back on the giving side while
 * leaving the ledger netted to zero. The subledger entry returns to the status
 * it held before the delete — an entry parked at `needs_allocation` or
 * `sync_failed` comes back parked rather than being promoted to postable.
 */
export async function undeleteContributionsWithPostings({
  organizationId,
  contributionIds,
  reason,
}: {
  organizationId: string;
  contributionIds: string[];
  reason?: string;
}): Promise<ContributionUndeleteResult> {
  if (!organizationId) {
    throw new Error("undeleteContributionsWithPostings requires organizationId");
  }
  const ids = cleanContributionIds(contributionIds);
  if (ids.length === 0) {
    return { restoredIds: [], undeleted: 0, restored: 0, unvoidedUnposted: 0 };
  }

  const { data, error } = (await supabaseRpcUntyped.rpc(
    "finance_undelete_contributions_with_postings",
    {
      p_organization_id: organizationId,
      p_ids: ids,
      p_reason: reason ?? null,
    },
  )) as { data: LedgerRpcResult | null; error: { message?: string } | null };

  if (error) {
    console.error("Error undeleting contributions with GL restore:", error);
    throw new Error(error.message || "Failed to restore contributions");
  }

  return {
    restoredIds: Array.isArray(data?.restored_ids)
      ? data.restored_ids.filter((id): id is string => typeof id === "string")
      : [],
    undeleted: toCount(data?.undeleted),
    restored: toCount(data?.restored),
    unvoidedUnposted: toCount(data?.unvoided_unposted),
  };
}

/**
 * Reverse the GL postings of contributions that are ALREADY deleted, without
 * touching `deleted_at`. This is the repair path for gifts removed before the
 * reversal existed; ordinary deletion goes through
 * {@link deleteContributionsWithReversal}, which is atomic with the delete.
 */
export async function reverseContributionPostings({
  organizationId,
  contributionIds,
  reason,
}: {
  organizationId: string;
  contributionIds: string[];
  reason?: string;
}): Promise<ContributionReversalResult> {
  if (!organizationId) {
    throw new Error("reverseContributionPostings requires organizationId");
  }
  const ids = cleanContributionIds(contributionIds);
  if (ids.length === 0) {
    return { reversed: 0, voidedUnposted: 0, skipped: 0 };
  }

  const { data, error } = (await supabaseRpcUntyped.rpc("finance_reverse_contribution_postings", {
    p_org_id: organizationId,
    p_contribution_ids: ids,
    p_reason: reason ?? null,
  })) as { data: LedgerRpcResult | null; error: { message?: string } | null };

  if (error) {
    console.error("Error reversing contribution GL postings:", error);
    throw new Error(error.message || "Failed to reverse contribution postings");
  }

  return {
    reversed: toCount(data?.reversed),
    voidedUnposted: toCount(data?.voided_unposted),
    skipped: toCount(data?.skipped),
  };
}

/**
 * Reverse every orphaned GL posting in an organization: contributions that are
 * already soft-deleted but whose subledger entry has not been voided. This is
 * the one-shot repair for ledgers that predate the reversal path.
 */
export async function reverseOrphanedContributionPostings({
  organizationId,
  reason,
}: {
  organizationId: string;
  reason?: string;
}): Promise<ContributionReversalResult> {
  if (!organizationId) {
    throw new Error("reverseOrphanedContributionPostings requires organizationId");
  }

  const { data, error } = (await supabaseRpcUntyped.rpc(
    "finance_reverse_orphaned_contribution_postings",
    {
      p_org_id: organizationId,
      ...(reason ? { p_reason: reason } : {}),
    },
  )) as { data: LedgerRpcResult | null; error: { message?: string } | null };

  if (error) {
    console.error("Error repairing orphaned contribution GL postings:", error);
    throw new Error(error.message || "Failed to repair orphaned contribution postings");
  }

  return {
    reversed: toCount(data?.reversed),
    voidedUnposted: toCount(data?.voided_unposted),
    skipped: toCount(data?.skipped),
  };
}
